import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, isCanceled, logApiError } from 'services/apiError'
import useAbortController from 'hooks/useAbortController'
import ItemTabs from 'components/ItemTabs'
import PaymentForm from 'components/Forms/PaymentForm'
import CheckBox from 'components/CheckBox'
import AlertDialog from 'components/AlertDialog'
import BillScheduleList from 'components/Lists/BillScheduleList'
import BillServiceList from 'components/Lists/BillServiceList'
import BillProductList from 'components/Lists/BillProductList'
import BillBundleList from 'components/Lists/BillBundleList'
import BillTipList from 'components/Lists/BillTipList'
import BillServiceForm from 'components/Forms/BillServiceForm'
import BillProductForm from 'components/Forms/BillProductForm'
import BillBundleForm from 'components/Forms/BillBundleForm'
import BillTipForm from 'components/Forms/BillTipForm'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import paymentValidator from 'validators/paymentValidator'
import { hydrateMachinePayment, withoutMachineSentinel } from 'services/CardMachine'
import { TbFreeRights } from 'react-icons/tb'
import { FaPlus, FaSave, FaTimes, FaTrash } from 'react-icons/fa'
import { Formik } from 'formik'
import { DateTime } from 'luxon'

import {
  Form,
  FormField,
  InfoContainer,
  Info,
  Row,
  Label,
  SubTitle,
  InfoText,
  MoneyTextMask,
  ErrorContainer
} from './styles'

const isDebtPayment = payment => payment?.paymentMethod?.value?.codename === 'debt'

// Quanto um pagamento conta para o total da comanda. Com a taxa repassada ao cliente, `value`
// é o valor engordado para a maquininha (ver services/CardMachine.js), não o que a comanda deve
// considerar pago — contá-lo integral geraria crédito indevido do tamanho do acréscimo. Nesse
// caso vale `netValue`, que o formulário mantém igual ao preço original.
const billAmountOf = payment => (
  payment.feeChargedToCustomer ? (payment.netValue ?? payment.value) : payment.value
)

export default function EditBillModal ({
  visible,
  data: bill,
  showFreeItemsValue = false,
  onDeleteSchedule: handleDeleteSchedule = () => {},
  onEditScheduleStatus: handleEditScheduleStatus = () => {},
  onConfirmEdit: handleConfirmEdit = () => {},
  onCloseBill: handleCloseBill = () => {},
  onClose: handleClose
}) {
  const [option, setOption] = useState ('items')
  const [loading, setLoading] = useState (true)
  const [loadedBillId, setLoadedBillId] = useState (null)
  const [loadError, setLoadError] = useState (null)
  const [activeSchedules, setActiveSchedules] = useState ([])
  const [payments, setPayments] = useState ([])
  const [paymentMethods, setPaymentMethods] = useState ([])
  const [bankAccounts, setBankAccounts] = useState ([])
  const [cardMachines, setCardMachines] = useState ([])
  const [services, setServices] = useState ([])
  const [products, setProducts] = useState ([])
  const [bundles, setBundles] = useState ([])
  const [professionals, setProfessionals] = useState ([])
  const [totalBalance, setTotalBalance] = useState (0)
  const [billServices, setBillServices] = useState ([])
  const [billProducts, setBillProducts] = useState ([])
  const [billBundles, setBillBundles] = useState ([])
  const [billTips, setBillTips] = useState ([])
  const [freeItems, setFreeItems] = useState ([])
  const [billTotal, setBillTotal] = useState (0)
  const [useCredits, setUseCredits] = useState (false)
  const [showServiceForm, setShowServiceForm] = useState (false)
  const [showProductForm, setShowProductForm] = useState (false)
  const [showBundleForm, setShowBundleForm] = useState (false)
  const [showTipForm, setShowTipForm] = useState (false)
  const [showAlertDialog, setShowAlertDialog] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const hasOpenBillEditPermission = AuthService.hasPermission ('financial.open_bills', 'edit') && bill.open
  const hasClosedBillEditPermission = AuthService.hasPermission ('financial.closed_bills', 'edit') && !bill.open
  const hasOpenBillDeletePermission = AuthService.hasPermission ('financial.open_bills', 'delete') && bill.open
  const hasClosedBillDeletePermission = AuthService.hasPermission ('financial.closed_bills', 'delete') && !bill.open
  const businessId = AuthService.getBusinessId ()
  const isProfessional = AuthService.isProfessional ()
  const token = AuthService.getToken ()
  const user = AuthService.getUser ()

  const creditsUsed = useMemo (() => {
    if (!useCredits) return 0
    if (totalBalance > 0) return Math.min (totalBalance, billTotal)
    if (totalBalance < 0) return totalBalance
    return 0
  }, [useCredits, totalBalance, billTotal])

  const totalWithDiscount = useMemo (() => {
    if (!useCredits) return billTotal
    return billTotal - creditsUsed
  }, [billTotal, creditsUsed, useCredits])

  const TAB_LIST = [
    {label: 'Itens', value: 'items'},
    {label: 'Pagamento', value: 'payment'}
  ]

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const { getSignal } = useAbortController ()

  useEffect (() => {
    setOption ('items')
    if (!!visible) getData ()
  }, [bill, visible])

  useEffect (() => {
    calculateBillValue ()
  }, [activeSchedules, billServices, billProducts, billBundles, billTips, freeItems])

  const getData = async () => {
    setLoading (true)
    setErrorMessage (null)
    setLoadError (null)

    try {
      // Fechar a comanda antes das 16 respostas voltarem cancela o que ficou em voo.
      const config = { ...requestConfig, signal: getSignal () }

      const scheduleFilters = `business_id=${businessId}&bill_id=${bill.id}&per_page=999&cutilei_schedules=false`
                            + (isProfessional ? '&show_all_professionals=true' : '')

      const [
        { data: schedules }, { data: billServices }, { data: billProducts }, { data: billBundles },
        { data: billTips }, { data: payments }, { data: credits }, { data: debts }, { data: paymentMethods },
        { data: bankAccounts }, { data: services }, { data: products }, { data: bundles },
        { data: professionals }, { data: freeServices }, { data: cardMachines },
      ] = await Promise.all ([
        cutileiApi.get (`/schedules?${scheduleFilters}`, config),
        cutileiApi.get (`/business_bills/${bill.id}/services`, config),
        cutileiApi.get (`/business_bills/${bill.id}/products`, config),
        cutileiApi.get (`/business_bills/${bill.id}/bundles`, config),
        cutileiApi.get (`/tips?bill_id=${bill.id}`, config),
        cutileiApi.get (`/payments?bill_id=${bill.id}`, config),
        cutileiApi.get (`/businesses/${businessId}/credits?bill_id=${bill.id}`, config),
        cutileiApi.get (`/businesses/${businessId}/debts?bill_id=${bill.id}`, config),
        cutileiApi.get ('/payment_methods', config),
        cutileiApi.get (`/businesses/${businessId}/bank_accounts`, config),
        cutileiApi.get (`/businesses/${businessId}/services?cutilei_services=false`, config),
        cutileiApi.get (`/businesses/${businessId}/products?used_in_service=false`, config),
        cutileiApi.get (`/businesses/${businessId}/bundles`, config),
        cutileiApi.get (`/businesses/${businessId}/professionals?status=active&deleted=false&access_level=service`, config),
        cutileiApi.get (`/customers/${bill.customer.id}/services?business_id=${businessId}&available=true`, config),
        // Sem maquininha cadastrada (ou sem acesso à lista) a comanda segue só com o fluxo de bandeira.
        cutileiApi.get (`/businesses/${businessId}/card_machines`, config).catch (() => ({ data: [] }))
      ])

      const creditTotal = credits.reduce ((total, credit) => total + credit.value, 0)
      const debtTotal = debts.reduce ((total, debt) => total + debt.value, 0)
      const billCreditsUsed = bill.credits_used ?? 0
      const totalBalance = bill.customer.balance - creditTotal + debtTotal + billCreditsUsed

      setUseCredits (billCreditsUsed !== 0)
      setActiveSchedules (schedules.data.filter (s => s.bill_active))
      setBillServices (billServices)
      setBillProducts (billProducts)
      setBillBundles (billBundles)
      setBillTips (billTips)
      setPayments (payments)
      setPaymentMethods (paymentMethods)
      setBankAccounts (bankAccounts)
      setCardMachines (cardMachines ?? [])
      setServices (services)
      setProducts (products)
      setBundles (bundles)
      setProfessionals (professionals)
      setFreeItems (freeServices.map (s => ({ ...s, isFree: true })))
      setTotalBalance (totalBalance)
      setLoadedBillId (bill.id)
    } catch (error) {
      // A comanda foi fechada antes das respostas voltarem: não é falha de carga.
      if (isCanceled (error)) return

      logApiError (error)
      setLoadError ('Não foi possível carregar esta comanda')
    } finally {
      setLoading (false)
    }
  }

  const handleAddPayment = (payments, setFieldValue) => {
    setFieldValue ('payments', [
      ...payments,
      {
        value: '',
        paymentMethod: null,
        installments: null,
        installmentValue: null
      }
    ])
  }

  const handleDeletePayment = (payments, index, setFieldValue) => {
    setFieldValue (
      'payments',
      payments.map ((p, i) => ({...p, idx: i})).filter (p => p.idx !== index)
    )
  }

  const hasItems = useMemo (() => (
    activeSchedules.length > 0 || billServices.length > 0 ||
    billProducts.length > 0 || billBundles.length > 0 || billTips.length > 0
  ), [activeSchedules, billServices, billProducts, billBundles, billTips])

  const canEditBill = useMemo (() => (
    bill.cash_register.open && (hasOpenBillEditPermission || hasClosedBillEditPermission)
  ), [bill.cash_register.open, hasOpenBillEditPermission, hasClosedBillEditPermission])

  const hasFreeServices = useMemo (() => {
    const freeItemIDs = freeItems.filter (s => s.isFree).map (s => s.id)
    const bundlesServiceIDs = billBundles.reduce ((acc, bundle) => {
      return acc.concat (bundle.services.map (s => s.id))
    }, [])

    return (
      activeSchedules.some (schedule => freeItemIDs.includes (schedule.service.id)) ||
      billServices.some (service => (
        freeItemIDs.includes (service.id) || bundlesServiceIDs.includes (service.id)
      ))
    )
  }, [freeItems, billBundles, activeSchedules, billServices])

  const defaultProfessional = useMemo (() => {
    const scheduleProfessionals = activeSchedules
      .map (s => s.professional)
      .filter (Boolean)

    const uniqueProfessionals = Array.from (
      new Map (scheduleProfessionals.map (p => [p.id, p])).values ()
    )

    return uniqueProfessionals.length === 1
      ? { label: uniqueProfessionals[0].nickname, value: uniqueProfessionals[0].id }
      : null
  }, [activeSchedules])
  
  const updateBillValue = async newValue => {
    if (loading) {
      setBillTotal (bill.value)
      return
    }

    setBillTotal (newValue)

    if (!canEditBill) return
    if (Math.abs (newValue - bill.value) < 0.005) return

    try {
      await cutileiApi.put (`/business_bills/${bill.id}`, {
        value: newValue
      }, requestConfig)

      handleConfirmEdit ({ ...bill, value: newValue })
    } catch (error) {
      logApiError (error)
      setErrorMessage (getApiErrorMessage (error))
    }
  }
  
  const calculateBillValue = () => {
    const sumValues = items => (
      items.length > 0
        ? items.map (i => i.custom_price ?? i.price ?? i.value).reduce ((tot, val) => tot + val)
        : 0
    )

    const filterFreeServices = items => {
      const freeItemIDs = freeItems.filter (s => s.isFree).map (s => s.id)
      const bundlesServiceIDs = billBundles.reduce ((acc, bundle) => {
        return acc.concat (bundle.services.map (s => s.id))
      }, [])

      return items.filter (i => {
        const serviceID = i.service?.id ?? i.id
        return (
          !freeItemIDs.includes (serviceID) && !bundlesServiceIDs.includes (serviceID)
        )
      })
    }

    const scheduleTotal = sumValues (filterFreeServices (activeSchedules))
    const serviceTotal = sumValues (filterFreeServices (billServices))
    const productTotal = sumValues (billProducts)
    const bundleTotal = sumValues (billBundles)
    const tipTotal = sumValues (billTips)
    const billTotal = scheduleTotal + serviceTotal + productTotal + bundleTotal + tipTotal
    updateBillValue (billTotal)
  }

  const splitBillServices = (addedServices, removedServices, removedSchedules) => {
    setActiveSchedules (activeSchedules.filter (schedule => (
      !removedSchedules.some (s => s.id === schedule.id)
    )))
    
    setBillServices (billServices.filter (service => (
      !removedServices.some (s => s.id === service.id)
    )).concat (addedServices))
  }

  const toggleFreeItem = (item, isFree) => {
    setFreeItems (freeItems.map (s => (
      s.id === item.id || s.id === item.service_id ? { ...s, isFree } : s
    )))
  }

  const toggleAlertDialog = () => setShowAlertDialog (!showAlertDialog)
  const toggleServiceForm = () => setShowServiceForm (!showServiceForm)
  const toggleProductForm = () => setShowProductForm (!showProductForm)
  const toggleBundleForm = () => setShowBundleForm (!showBundleForm)
  const toggleTipForm = () => setShowTipForm (!showTipForm)

  const ITEM_OPTIONS = [
    {label: 'Serviço', value: 'service', onClick: toggleServiceForm, selected: showServiceForm},
    {label: 'Produto', value: 'product', onClick: toggleProductForm, selected: showProductForm},
    {label: 'Pacote', value: 'bundle', onClick: toggleBundleForm, selected: showBundleForm},
    {label: 'Caixinha', value: 'tip', onClick: toggleTipForm, selected: showTipForm}
  ]

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='2xl' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            Comanda Nº{bill.number} - {bill.customer.name} -
            Aberta em {DateTime.fromISO (bill.date).toFormat ('dd/MM/yyyy')}
          </DialogTitle>
        </DialogHeader>
        {/* As abas ficam paradas entre o título e o corpo, que rola */}
        <div style={{paddingInline: 24}}>
          <ItemTabs
            tabs={TAB_LIST}
            selectedTab={option}
            onTabChange={setOption}
            containerStyle={{paddingTop: 0, marginBottom: 5}}
          />
        </div>
        {loading || loadedBillId !== bill.id ? (
          <DialogBody>
            {loadError ? (
              <ErrorContainer>
                <InfoText>{loadError}</InfoText>
              </ErrorContainer>
            ) : <Loading/>}
          </DialogBody>
        ) : (
          <Formik
            enableReinitialize={true}
            validationSchema={canEditBill && totalWithDiscount > 0 ? paymentValidator : null}
            initialValues={{
              'payments': payments.length > 0 ? payments.map (payment => ({
                id: payment.id,
                value: payment.value,
                description: payment.description ?? '',
                paymentMethod: {
                  label: payment.payment_method.name,
                  value: payment.payment_method
                },
                bankAccount: payment.bank_account ? {
                  label: `(${payment.bank_account.bank.code} ${payment.bank_account.bank.name})`,
                  value: payment.bank_account 
                } : null,
                installments: payment.installments,
                installmentValue: payment.installmentValue,
                ...hydrateMachinePayment (payment, cardMachines)
              })) : [
                {
                  'value': 0,
                  'description': '',
                  'paymentMethod': null,
                  'bankAccount': null,
                  'installments': null,
                  'installmentValue': null
                }
              ]
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                // A linha de "Dívida" vai junto de propósito: o servidor não a grava
                // como pagamento, mas recusa fechar uma comanda sem pagamento algum
                // quando ela não vem — é o que separa "o cliente ficou devendo" de
                // "o operador esqueceu de lançar o pagamento".
                const { data: closedBillResponse } = await cutileiApi.post (
                  `/business_bills/${bill.id}/close`,
                  { payments: withoutMachineSentinel (values.payments), totalWithDiscount, creditsUsed, freeItems },
                  requestConfig
                )

                const { bill: closedBill, billSchedules } = closedBillResponse

                setSubmitting (false)
                handleCloseBill (closedBill)
                handleEditScheduleStatus (billSchedules.map (s => ({
                  ...s, status: 'paid', validated: true
                })))
                handleClose ()
              } catch (error) {
                logApiError (error)
                setErrorMessage (getApiErrorMessage (error))
                setSubmitting (false)
              }
            }}
          >
            {function BillForm (formikProps) {
              useEffect (() => {
                if (totalWithDiscount === 0 && payments.length === 0) {
                  formikProps.setFieldValue ('payments', [{
                    'value': 0,
                    'description': '',
                    'paymentMethod': null,
                    'bankAccount': null,
                    'installments': null,
                    'installmentValue': null
                  }])
                }
              }, [totalWithDiscount, payments.length])

              const paymentTotal = useMemo (() => (
                formikProps.values.payments
                  .filter (p => !isDebtPayment (p))
                  .map (billAmountOf).reduce ((tot, val) => tot + val, 0)
              ), [formikProps.values.payments])

              const finalValue = paymentTotal - totalWithDiscount

              // A dívida vale o que faltou pagar, e não o que foi digitado nela: quem
              // grava a dívida do cliente é o servidor, pela diferença entre o total e
              // os pagamentos, e as duas contas têm que dizer a mesma coisa.
              useEffect (() => {
                const debtIndex = formikProps.values.payments.findIndex (isDebtPayment)
                if (debtIndex < 0) return

                const missingValue = Math.max (-finalValue, 0)

                if (Math.abs (formikProps.values.payments[debtIndex].value - missingValue) < 0.005) return
                formikProps.setFieldValue (`payments[${debtIndex}].value`, missingValue)
              }, [finalValue, formikProps.values.payments])

              return (
                <>
                  <DialogBody>
                    <Form>
                      {option === 'items' && (
                        <Info>
                          <BillScheduleList
                            schedules={activeSchedules}
                            professionals={professionals}
                            services={services}
                            freeItems={freeItems}
                            showFreeItemsValue={showFreeItemsValue}
                            enableEditting={bill.cash_register.open}
                            hasEdiPermission={hasOpenBillEditPermission || hasClosedBillEditPermission}
                            hasDeletePermission={hasOpenBillDeletePermission || hasClosedBillDeletePermission}
                            onToggleFree={toggleFreeItem}
                            onEdit={schedule => {
                              setActiveSchedules (activeSchedules.map (s => s.id === schedule.id ? {...schedule} : s))
                            }}
                            onDelete={schedule => {
                              setActiveSchedules (activeSchedules.filter (s => s.id !== schedule.id))
                              handleDeleteSchedule (schedule)
                            }}
                            onRemoveFromBill={
                              schedule => setActiveSchedules (activeSchedules.filter (s => s.id !== schedule.id))
                            }
                          />
                          <BillServiceList
                            bill={bill}
                            billServices={billServices}
                            freeItems={freeItems}
                            showFreeItemsValue={showFreeItemsValue}
                            professionals={professionals}
                            enableEditting={bill.cash_register.open}
                            hasEdiPermission={hasOpenBillEditPermission || hasClosedBillEditPermission}
                            hasDeletePermission={hasOpenBillDeletePermission || hasClosedBillDeletePermission}
                            onToggleFree={toggleFreeItem}
                            onEdit={service => {
                              setBillServices (billServices.map (s => s.id === service.id ? {...service} : s))
                            }}
                            onDelete={service => setBillServices (billServices.filter (s => s.id !== service.id))}
                          />
                          <BillProductList
                            bill={bill}
                            billProducts={billProducts}
                            professionals={professionals}
                            enableEditting={bill.cash_register.open}
                            hasEdiPermission={hasOpenBillEditPermission || hasClosedBillEditPermission}
                            hasDeletePermission={hasOpenBillDeletePermission || hasClosedBillDeletePermission}
                            onEdit={product => {
                              setBillProducts (billProducts.map (p => p.id === product.id ? {...product} : p))
                            }}
                            onDelete={product => setBillProducts (billProducts.filter (p => p.id !== product.id))}
                          />
                          <BillBundleList
                            bill={bill}
                            billBundles={billBundles}
                            professionals={professionals}
                            enableEditting={bill.cash_register.open}
                            hasEdiPermission={hasOpenBillEditPermission || hasClosedBillEditPermission}
                            hasDeletePermission={hasOpenBillDeletePermission || hasClosedBillDeletePermission}
                            onEdit={bundle => {
                              setBillBundles (billBundles.map (b => b.id === bundle.id ? {...bundle} : b))
                              setFreeItems ([
                                ...freeItems.filter (s => s.bundle_id !== bundle.id),
                                ...bundle.services.map (s => ({ ...s, isFree: true }))
                              ])
                            }}
                            onDelete={bundle => {
                              setBillBundles (billBundles.filter (b => b.id !== bundle.id))
                              setFreeItems (freeItems.filter (s => s.bundle_id !== bundle.id))
                            }}  
                          />
                          <BillTipList
                            bill={bill}
                            billTips={billTips}
                            professionals={professionals}
                            enableEditting={bill.cash_register.open}
                            hasEdiPermission={hasOpenBillEditPermission || hasClosedBillEditPermission}
                            hasDeletePermission={hasOpenBillDeletePermission || hasClosedBillDeletePermission}
                            onEdit={tip => setBillTips (billTips.map (t => t.id === tip.id ? {...tip} : t))}
                            onDelete={tip => setBillTips (billTips.filter (t => t.id !== tip.id))}
                          />
                          {!hasItems && (
                            <InfoText style={{color: '#000000', marginBottom: 20}}>
                              Esta comanda ainda não possui itens
                            </InfoText>
                          )}
                          {showServiceForm && (
                            <BillServiceForm
                              bill={bill}
                              services={services}
                              professionals={professionals}
                              defaultProfessional={defaultProfessional}
                              freeItems={freeItems}
                              onConfirm={services => setBillServices ([...billServices, ...services])}
                              onClose={toggleServiceForm}
                            />
                          )}
                          {showProductForm && (
                            <BillProductForm
                              bill={bill}
                              professionals={professionals}
                              products={products}
                              onConfirm={product => setBillProducts ([...billProducts, product])}
                              onClose={toggleProductForm}
                            />
                          )}
                          {showBundleForm && (
                            <BillBundleForm
                              bill={bill}
                              professionals={professionals}
                              bundles={bundles}
                              services={services}
                              billSchedules={activeSchedules}
                              billServices={billServices}
                              onConfirm={bundle => {
                                setBillBundles ([...billBundles, bundle])
                                setFreeItems ([
                                  ...freeItems,
                                  ...bundle.services.map (s => ({ ...s, bundle_id: bundle.id, isFree: true }))
                                ])
                              }}
                              onSplitServices={splitBillServices}
                              onClose={toggleBundleForm}
                            />
                          )}
                          {showTipForm && (
                            <BillTipForm
                              bill={bill}
                              professionals={professionals}
                              defaultProfessional={defaultProfessional}
                              onConfirm={tip => setBillTips ([...billTips, tip])}
                              onClose={toggleTipForm}
                            />
                          )}
                          {canEditBill && (
                            <Row style={{marginBottom: 0}}>
                              {ITEM_OPTIONS.map (option => (
                                <SecondaryButton
                                  key={option.value}
                                  label={option.label}
                                  onClick={option.onClick}
                                  leftIcon={
                                    option.selected
                                      ? <FaTimes size={14} color='#FFFFFF' style={{marginBottom: 1}}/>
                                      : <FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>
                                  }
                                  color={option.selected ? '#FF3939' : '#252525'}
                                  width={120}
                                />
                              ))}
                            </Row>
                          )}
                        </Info>
                      )}
                      {option === 'payment' && (
                        <Info>
                          {hasFreeServices && (
                            <Row style={{gap: 7}}>
                              <TbFreeRights size={24} color='#FFC100'/>
                              <SubTitle style={{marginBottom: 0}}>
                                Este cliente possui serviços de pacotes adquiridos ou cartões fidelidade que não serão cobrados
                              </SubTitle>
                            </Row>
                          )}
                          <InfoContainer>
                            <FormField>
                              <Label>Caixa responsável</Label>
                              <SubTitle style={{marginBottom: 7}}>
                                {bill.cash_register.opened_by?.name || user.name}
                                <br/>
                                ({DateTime.fromISO (bill.cash_register.created_at).toFormat ('dd/MM/yyyy H:mm')})
                              </SubTitle>
                            </FormField>
                            <FormField>
                              <Label>Créditos / dívidas</Label>
                              <MoneyTextMask value={totalBalance - creditsUsed}/>
                              {bill.cash_register.open && totalBalance !== 0 && (
                                <Row style={{alignItems: 'center'}}>
                                  <CheckBox
                                    id='useCredits'
                                    style={{marginRight: -3}}
                                    value={useCredits}
                                    onValueChange={setUseCredits}
                                    disabled={!(hasOpenBillEditPermission || hasClosedBillEditPermission)}
                                  />
                                  <label htmlFor='useCredits' style={{cursor: 'pointer'}}>
                                    Somar {totalBalance > 0 ? 'crédito' : 'dívida'} à comanda
                                  </label>
                                </Row>
                              )}
                            </FormField>
                            <FormField>
                              <Label>Total da comanda</Label>
                              <MoneyTextMask value={billTotal ?? 0}/>
                            </FormField>
                            <FormField>
                              <Label>Total a cobrar</Label>
                              <MoneyTextMask
                                value={totalWithDiscount ?? 0}  
                                style={{color: formikProps.values.payments.some (p => (
                                  p.paymentMethod?.value.codename === 'debt'
                                )) ? '#FF3939' : '#44CF6C'}}
                              />
                            </FormField>
                            <FormField>
                              <Label>Troco</Label>
                              <MoneyTextMask value={(finalValue) ?? 0}/>
                            </FormField>
                          </InfoContainer>
                          <SubTitle>Pagamentos</SubTitle>
                          {formikProps.values.payments.map ((_, index) => (
                            <Row key={index}>
                              <PaymentForm
                                index={index}
                                formikProps={formikProps}
                                edittingEnabled={canEditBill}
                                paymentMethods={paymentMethods}
                                bankAccounts={bankAccounts}
                                cardMachines={cardMachines}
                                billValue={totalWithDiscount}
                              />
                              {formikProps.values.payments.length > 1 && canEditBill && (
                                <SecondaryButton
                                  onClick={() => {
                                    handleDeletePayment (formikProps.values.payments, index, formikProps.setFieldValue)
                                  }}
                                  leftIcon={<FaTrash size={12} color='#FFFFFF'/>}
                                  color='#FF3939'
                                  width={60}
                                />
                              )}
                            </Row>
                          ))}
                          {canEditBill && (
                            <SecondaryButton
                              label='Método de pagamento'
                              onClick={() => handleAddPayment (formikProps.values.payments, formikProps.setFieldValue)}
                              leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>}
                              color='#252525'
                              width={200}
                            />
                          )}
                          {errorMessage && (
                            <ErrorContainer>
                              <InfoText>{errorMessage}</InfoText>
                            </ErrorContainer>
                          )}
                          {!bill.cash_register.open && (
                            <ErrorContainer>
                              <InfoText>Uma comanda só pode ser editada se o caixa estiver aberto</InfoText>
                            </ErrorContainer>
                          )}
                          <AlertDialog
                            visible={showAlertDialog}
                            title='Atenção!'
                            message={
                              <InfoText style={{color: '#000000'}}>
                                {'Ao salvar esta comanda, ela será finalizada'
                                + (finalValue !== 0 ? (
                                  ` e irá gerar ${finalValue > 0 ? 'um crédito' : 'uma dívida'} `
                                  + `no valor de R$ ${Math.abs (finalValue)} para o cliente. `
                                ) : '. ')
                                + 'Deseja realmente prosseguir?'}
                              </InfoText>
                            }
                            confirmText='Sim'
                            cancelText='Não'
                            onConfirm={formikProps.handleSubmit}
                            onClose={toggleAlertDialog}
                          />
                        </Info>
                      )}
                    </Form>
                  </DialogBody>
                  {option === 'payment' && canEditBill && (
                    <DialogFooter>
                      <MainButton
                        label='Salvar e finalizar'
                        leftIcon={<FaSave size={14} color='#252525' style={{marginBottom: 2}}/>}
                        onClick={async () => {
                          let errors = null
                          await Promise.all (formikProps.values.payments.map (async (_, index) => {
                            await formikProps.setFieldTouched (`payments[${index}].cardMachine`)
                            await formikProps.setFieldTouched (`payments[${index}].brand`)
                            await formikProps.setFieldTouched (`payments[${index}].modality`)
                            errors = await formikProps.setFieldTouched (`payments[${index}].paymentMethod`)
                          }))
                          if (!errors?.payments?.length > 0) toggleAlertDialog ()
                        }}
                        disabled={formikProps.isSubmitting}
                        loading={formikProps.isSubmitting}
                        width={200}
                      />
                    </DialogFooter>
                  )}
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
